import { useStore } from 'jotai/index'
import { useEffect } from 'react'
import { CourseSelection } from '~/components/fusuma/courseSelection'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import { CardModal } from '~/features/reservationInput/card/CardFusuma'
import { ChildMenusModal } from '~/features/reservationInput/childMenu'
import { OptionMenusModal } from '~/features/reservationInput/optionMenu/OptionMenuFusuma'
import { CancelPolicyBody } from '~/features/reservationInput/sd/cancelPolicy/CancelPolicyBody'
import { NotesBody } from '~/features/reservationInput/sd/notes/NotesBody'
import { useCloseNewReserveInput } from '~/features/reservationInput/shared/hooks'
import { error$ } from '~/features/reservationInput/store/error'
import { reservationInputState$ } from '~/features/reservationInput/store/stateMachine/context'
import { reservationInputMachine } from '~/features/reservationInput/store/stateMachine/statemachine'
import { hasUserInteracted$ } from '~/features/reservationInput/store/validate'
import { TotalCountModal } from '~/features/reservationInput/totalCount/TotalCount'
import { useIsPC } from '~/lib/ua'
import { useReserveOverlay, useReserveOverlayContext } from '~/navigation'
import type { CourseSelectContext } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { Body, RequestBeacon } from '../sd'
import { InitAtom } from '../sd/InitAtom'
import { CourseDetailModal } from '../sd/plan/CourseDetailModal'
import { ReservationError } from '../sd/reservationError'
import { SeatDetailModal } from '../sd/seatsSelect'
import { scope } from '../store/scope'
import { ReservationSuccessModal } from './ReservationSuccessModal'
import { StepTitle } from './StepTitle'

export function ReserveSteps() {
  const context = useReserveOverlayContext(
    'COURSE_SELECT',
    'NEW_RESERVE_INPUT',
    'NEW_RESERVE_SUCCESS',
    'NEW_RESERVE_ERROR'
  )
  if (context === undefined) {
    return null
  }
  return <Root />
}

function Root() {
  return (
    <scope.Provider>
      <Init />
      <ReserveStepsModal />
      <ChildMenusModal />
      <TotalCountModal />
      <CourseDetailModal />
      <SeatDetailModal />
      <OptionMenusModal />
      <CardModal />
      <CancelPolicyModal />
      <NotesModal />
      <ReservationSuccessModal />
    </scope.Provider>
  )
}

function ReserveStepsModal() {
  // コース選択・予約入力のときにこのモーダルダイアログを表示させる
  const [context, willBeUnmounted] = useReserveOverlay(
    'COURSE_SELECT',
    'NEW_RESERVE_INPUT',
    'NEW_RESERVE_ERROR'
  )
  const isReserve = useReserveOverlayContext('NEW_RESERVE_INPUT')
  const isError = useReserveOverlayContext('NEW_RESERVE_ERROR')
  const onClose = useCloseNewReserveInput()
  if (context === undefined) {
    return null
  }
  // TODO Swiper で動きをつけたい
  return (
    <StackableSlideUpModal type="full" willBeUnmounted={willBeUnmounted} onClose={onClose}>
      <div className={css({ borderBottom: 'medium' })}>
        <StepTitle
          state={
            isReserve !== undefined || isError !== undefined ? 'NEW_RESERVE_INPUT' : 'COURSE_SELECT'
          }
        />
      </div>
      {isError ? (
        <ReservationError />
      ) : isReserve ? (
        <ReserveInput />
      ) : (
        <CourseSelection context={context as CourseSelectContext} />
      )}
    </StackableSlideUpModal>
  )
}

function ReserveInput() {
  // ScopeProvider の中の store を得ているので安全に更新できる
  const store = useStore()
  // PC の場合、コース選択と予約入力がステップ式のひとつのモーダルになるので、
  // 両方が ScopeProvider の子コンポーネントとなり、予約入力からコース選択に戻ったときも、
  // 前回の予約入力の atom の状態が維持されてしまう。
  // ブラウザバックしてプランを選びなおし、再度予約入力に遷移したときに
  // ScopeProvider の atom を強制的に初期値に戻して、
  // プランの選び直しで atom が正しい状態とする。
  useEffect(() => {
    store.set(reservationInputState$, reservationInputMachine.initialState)
    store.set(hasUserInteracted$, false)
    store.set(error$, undefined)
  }, [store])

  return <Body />
}

function Init() {
  const context = useReserveOverlayContext('NEW_RESERVE_INPUT')
  if (context === undefined) {
    return null
  }
  const { restaurantId, planId } = context
  return (
    <>
      <RequestBeacon restaurantId={restaurantId} planId={planId} />
      <InitAtom context={context} />
    </>
  )
}

const modalStyles = sva({
  slots: ['container', 'title'],
  base: {
    container: {
      p: '3',
      _pc: {
        p: '6',
        pb: '10',
        spaceY: '5',
      },
    },
    title: {
      _pc: {
        textAlign: 'left',
        fontSize: '2xl',
        fontWeight: 'bold',
      },
    },
  },
})

export function CancelPolicyModal() {
  const isPC = useIsPC()
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_CANCEL_POLICY')
  if (context === undefined) {
    return null
  }
  const classes = modalStyles()
  return (
    <StackableSlideUpModal animationType="up" willBeUnmounted={willBeUnmounted}>
      <div className={classes.container}>
        {isPC && <h2 className={classes.title}>キャンセルポリシー</h2>}
        <CancelPolicyBody />
      </div>
    </StackableSlideUpModal>
  )
}

export function NotesModal() {
  const isPC = useIsPC()
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_NOTE')
  if (context === undefined) {
    return null
  }
  const classes = modalStyles()
  return (
    <StackableSlideUpModal animationType="up" willBeUnmounted={willBeUnmounted}>
      <div className={classes.container}>
        {isPC && <h2 className={classes.title}>注意事項</h2>}
        <NotesBody />
      </div>
    </StackableSlideUpModal>
  )
}
